var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLaptop } from '@fortawesome/free-solid-svg-icons/faLaptop';
import { faListOl } from '@fortawesome/free-solid-svg-icons/faListOl';
import { faCloudDownloadAlt } from '@fortawesome/free-solid-svg-icons/faCloudDownloadAlt';
import { faVolumeUp } from '@fortawesome/free-solid-svg-icons/faVolumeUp';
import { faAmazon } from '@fortawesome/free-brands-svg-icons/faAmazon';
import $ from '~/helpers/$';
import { treatSpaceOrEnterAsClick } from '~/helpers/events';
import { useIntl, FormattedMessage } from 'react-intl';
import OrderPrintCopy from './order-print-copy/order-print-copy';
import useTOCContext from '../toc-slideout/context';
import { useDialog } from '~/components/dialog/dialog';
import RecommendedCallout from './recommended-callout/recommended-callout';
import { useOpenGiveDialog } from './give-before-pdf/use-give-dialog';
import useCalloutCounter from './recommended-callout/use-callout-counter';
import trackLink from '../track-link';
import { useRexPortalLinkOrNot } from '~/helpers/rex-portal';
function IconAndText({ icon, text }) {
    return (React.createElement(React.Fragment, null,
        React.createElement(FontAwesomeIcon, { icon: icon }),
        React.createElement("span", { className: "text" }, text)));
}
function Option({ condition, children }) {
    if (!condition) {
        return null;
    }
    return React.createElement("div", { className: "option", children: children });
}
export function SimpleLinkOption(_a) {
    var { link, icon, text, children } = _a, linkOptions = __rest(_a, ["link", "icon", "text", "children"]);
    return (React.createElement(Option, { condition: link },
        React.createElement("a", Object.assign({ href: link }, linkOptions),
            React.createElement(IconAndText, { icon: icon, text: text })),
        children));
}
export function TocOption({ model }) {
    const { toggle, isOpen } = useTOCContext();
    const includeTOC = ['live', 'deprecated', 'new_edition_available'].includes(model.bookState);
    const intl = useIntl();
    const text = $.isPolish(model.title)
        ? 'Spis treści'
        : intl.formatMessage({ id: 'getit.toc' });
    if (!includeTOC) {
        return null;
    }
    function toggleToc(event) {
        toggle();
        event.preventDefault();
    }
    return (React.createElement("div", { className: "option toc-option" },
        React.createElement("a", { href: "/", role: "button", className: "show-toc", "aria-pressed": isOpen, onClick: toggleToc, onKeyDown: treatSpaceOrEnterAsClick },
            React.createElement(FontAwesomeIcon, { icon: faListOl }),
            React.createElement("span", { className: "text" }, text))));
}
export function WebviewOption({ model }) {
    var _a;
    const [showCallout, hideForever] = useCalloutCounter(model.slug);
    const intl = useIntl();
    const texts = {
        link: intl.formatMessage({ id: 'getit.webview.link' })
    };
    const isRex = Boolean(model.webviewRexLink);
    const webviewLink = useRexPortalLinkOrNot(model.webviewRexLink) || model.webviewLink;
    const iconAndTextArgs = {
        icon: faLaptop,
        text: $.isPolish(model.title) ? 'Zobacz w przeglądarce' : texts.link
    };
    const { GiveDialog, openGiveDialog } = useOpenGiveDialog();
    const trackDownload = React.useCallback((event) => {
        trackLink(event, model.id.toString());
    }, [model.id]);
    return (React.createElement(Option, { condition: !model.comingSoon && webviewLink },
        React.createElement("div", { className: "option-with-callout" },
            React.createElement("a", { href: webviewLink, "data-local": isRex, rel: "noreferrer", onClick: openGiveDialog },
                React.createElement(IconAndText, Object.assign({}, iconAndTextArgs))),
            React.createElement(GiveDialog, { link: webviewLink, variant: "View online", warning: (_a = model.contentWarningText) !== null && _a !== void 0 ? _a : undefined, track: "Online", onDownload: trackDownload, id: model.id.toString() }),
            showCallout && (React.createElement("div", { className: "callout recommended-callout" },
                React.createElement(RecommendedCallout, { title: model.rexCalloutTitle, blurb: model.rexCalloutBlurb, onPutAway: hideForever }))))));
}
export function PdfOption({ model }) {
    var _a;
    const polish = $.isPolish(model.title);
    const intl = useIntl();
    const pdfText = polish
        ? ' Pobierz PDF'
        : intl.formatMessage({ id: 'getit.pdf.download' });
    const sampleText = polish
        ? ' przykład'
        : intl.formatMessage({ id: 'getit.pdf.sample' });
    const text = pdfText + (model.comingSoon ? sampleText : '');
    const pdfLink = model.highResolutionPdfUrl; // low res option is obsolete now
    const { GiveDialog, openGiveDialog } = useOpenGiveDialog();
    const trackDownload = React.useCallback((event) => {
        trackLink(event, model.id.toString());
    }, [model.id]);
    return pdfLink ? (React.createElement(React.Fragment, null,
        React.createElement(SimpleLinkOption, { link: pdfLink, icon: faCloudDownloadAlt, text: text, onClick: openGiveDialog }),
        React.createElement(GiveDialog, { link: pdfLink, track: "PDF", onDownload: trackDownload, id: model.id.toString(), warning: (_a = model.contentWarningText) !== null && _a !== void 0 ? _a : undefined }))) : null;
}
export function usePrintCopyDialog() {
    const [Dialog, open] = useDialog();
    const PCDialog = React.useCallback(({ text, slug }) => (React.createElement(Dialog, { title: text },
        React.createElement(OrderPrintCopy, { slug: slug }))), [Dialog]);
    const onClick = React.useCallback((event) => {
        event.preventDefault();
        open();
    }, [open]);
    return { onClick, PCDialog };
}
export function BookshareOption({ model }) {
    return (React.createElement(SimpleLinkOption, { link: model.bookshareLink, icon: faVolumeUp, text: "Bookshare", "data-track": "Bookshare" }));
}
export function KindleOption({ model }) {
    const intl = useIntl();
    const header = intl.formatMessage({ id: 'getit.kindle.header' });
    const defaultDisclaimer = 'As an Amazon Associate we earn from qualifying purchases';
    return (React.createElement(SimpleLinkOption, { link: model.kindleLink, icon: faAmazon, text: header, "data-track": "Kindle" },
        React.createElement("div", { className: "disclaimer" },
            React.createElement(FormattedMessage, { id: "getit.kindle.disclaimer", defaultMessage: defaultDisclaimer }))));
}
export function CheggOption({ model }) {
    var _a;
    return (React.createElement(Option, { condition: (_a = model.cheggLink) !== null && _a !== void 0 ? _a : false },
        React.createElement("a", { href: model.cheggLink, "data-track": "Chegg Reader" },
            React.createElement("img", { className: "logo-img", src: "/dist/images/icons/Chegglogo.svg", alt: "" }),
            React.createElement("span", { className: "text" }, model.cheggLinkText))));
}
function useExpanderText(optionCount) {
    const intl = useIntl();
    const options = optionCount > 1
        ? intl.formatMessage({ id: 'options' })
        : intl.formatMessage({ id: 'option' });
    return {
        fewer: intl.formatMessage({ id: 'expander.fewer' }, { optionCount, options }),
        more: intl.formatMessage({ id: 'expander.more' }, { optionCount, options })
    };
}
export function OptionExpander({ expanded, additionalOptions, toggle }) {
    const text = useExpanderText(additionalOptions)[expanded ? 'fewer' : 'more'];
    const doToggle = React.useCallback((event) => {
        toggle();
        event.preventDefault();
    }, [toggle]);
    if (additionalOptions < 1) {
        return null;
    }
    return (React.createElement("div", { className: "option expander" },
        React.createElement("a", { href: ".", onClick: doToggle, onKeyDown: treatSpaceOrEnterAsClick, "aria-expanded": expanded }, text)));
}
